import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  IconButton,
  Container,
  useMediaQuery,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  CalendarMonth as CalendarIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import SwalWithA11yFix from "../../services/swalConfig";
import mainLogo from "../../images/main-logo.avif";

const Header = () => {
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [labsMenuAnchor, setLabsMenuAnchor] = useState(null);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleUserMenuOpen = (event) => {
    if (userMenuAnchor) {
      setUserMenuAnchor(null); // Close if already open
    } else {
      setUserMenuAnchor(event.currentTarget);
      setLabsMenuAnchor(null); // Close labs menu when opening user menu
    }
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLabsMenuOpen = (event) => {
    if (labsMenuAnchor) {
      setLabsMenuAnchor(null); // Close if already open
    } else {
      setLabsMenuAnchor(event.currentTarget);
      setUserMenuAnchor(null); // Close user menu when opening labs menu
    }
  };

  const handleLabsMenuClose = () => {
    setLabsMenuAnchor(null);
  };

  // Close dropdowns on Escape key
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setUserMenuAnchor(null);
        setLabsMenuAnchor(null);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleLogout = async () => {
    handleUserMenuClose();

    try {
      const result = await SwalWithA11yFix({
        title: "Sign Out",
        text: "Are you sure you want to sign out?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d32f2f",
        cancelButtonColor: "#757575",
        confirmButtonText: "Yes, Sign Out",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        logout();
        navigate("/signin");
      }
    } catch (error) {
      console.error("SweetAlert error:", error);
    }
  };

  const navigationItems = [
    { label: "Home", href: "https://wwww.analytikaldna.com/" },
    { label: "About Us", href: "https://www.analytikaldna.com/about-us" },
    { label: "Testimonials", href: "https://www.analytikaldna.com/testimonal" },
    { label: "Contact Us", href: "https://www.analytikaldna.com/contact-us" },
    { label: "Events", href: "https://www.analytikaldna.com/event" },
  ];

  const labsMenuItems = [
    { label: "View All Labs", href: "http://guide.analytikaldna.com/labs" },
    { label: "Lab Catalog", href: "/lab-catalog" },
  ];

  const NavItems = ({ mobile = false }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: mobile ? "column" : "row",
        gap: mobile ? 1 : 2,
        alignItems: mobile ? "flex-start" : "center",
        width: mobile ? "100%" : "auto",
      }}
    >
      {navigationItems.map((item) => (
        <Button
          key={item.label}
          component={RouterLink}
          to={item.href}
          sx={{
            color: mobile ? "#1b2a6b" : "#1b2a6b",
            textDecoration: "none",
            textTransform: "none",
            fontSize: { xs: "0.95rem", md: "0.85rem", lg: "1rem" },
            fontWeight: 500,
            position: "relative",
            transition: "all 0.3s ease",
            padding: mobile ? "10px 16px" : { md: "8px 6px", lg: "8px 12px" },
            whiteSpace: "nowrap",
            width: mobile ? "100%" : "auto",
            justifyContent: mobile ? "flex-start" : "center",
            "&:hover": {
              backgroundColor: mobile
                ? "rgba(27, 42, 107, 0.08)"
                : "rgba(27, 42, 107, 0.08)",
              color: "#1b2a6b",
              "&::after": {
                width: mobile ? 0 : "100%",
              },
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: mobile ? "auto" : -4,
              left: 0,
              width: 0,
              height: 2,
              backgroundColor: "#1b2a6b",
              transition: "width 0.3s ease",
              display: mobile ? "none" : "block",
            },
          }}
          onClick={mobile ? handleMobileMenuToggle : undefined}
        >
          {item.label}
        </Button>
      ))}

      {/* Labs Dropdown - Using Material-UI Menu */}
      <Box
        sx={{
          position: "relative",
          width: mobile ? "100%" : "auto",
          overflow: "visible",
        }}
      >
        <Button
          onClick={handleLabsMenuOpen}
          sx={{
            color: "#1b2a6b",
            textDecoration: "none",
            textTransform: "none",
            fontSize: { xs: "0.95rem", md: "0.85rem", lg: "1rem" },
            fontWeight: 500,
            position: "relative",
            transition: "all 0.3s ease",
            padding: mobile ? "10px 16px" : { md: "8px 6px", lg: "8px 12px" },
            whiteSpace: "nowrap",
            width: mobile ? "100%" : "auto",
            justifyContent: mobile ? "flex-start" : "center",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            "&:hover": {
              backgroundColor: mobile
                ? "rgba(27, 42, 107, 0.08)"
                : "rgba(27, 42, 107, 0.08)",
              color: "#1b2a6b",
              "&::after": {
                width: mobile ? 0 : "100%",
              },
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: mobile ? "auto" : -4,
              left: 0,
              width: 0,
              height: 2,
              backgroundColor: "#1b2a6b",
              transition: "width 0.3s ease",
              display: mobile ? "none" : "block",
            },
          }}
        >
          Labs
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              transition: "transform 0.3s ease",
              transform: Boolean(labsMenuAnchor)
                ? "rotate(180deg)"
                : "rotate(0deg)",
            }}
          />
        </Button>

        {Boolean(labsMenuAnchor) && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              left: mobile ? 0 : "50%",
              transform: mobile ? "none" : "translateX(-50%)",
              right: mobile ? 0 : "auto",
              backgroundColor: "#fff",
              borderRadius: mobile ? 0 : "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              minWidth: mobile ? "100%" : 200,
              mt: 0.5,
              zIndex: 1300,
              overflow: "hidden",
            }}
          >
            {labsMenuItems.map((item, index) => (
              <Box
                key={item.label}
                component={RouterLink}
                to={item.href}
                onClick={handleLabsMenuClose}
                sx={{
                  py: 1.2,
                  px: 2.5,
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  color: "#1b2a6b",
                  textDecoration: "none",
                  display: "block",
                  transition: "all 0.2s ease",
                  borderBottom:
                    index !== labsMenuItems.length - 1
                      ? "1px solid #f0f0f0"
                      : "none",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                {item.label}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );

  const AuthSection = ({ mobile = false }) => {
    if (user) {
      // User is logged in
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: mobile ? 1 : 2,
            flexDirection: mobile ? "column" : "row",
            width: mobile ? "100%" : "auto",
            alignSelf: mobile ? "stretch" : "auto",
          }}
        >
          {mobile && <Divider sx={{ width: "100%", my: 1 }} />}
          <Box
            sx={{
              position: "relative",
              width: mobile ? "100%" : "auto",
              overflow: "visible",
            }}
          >
            <Button
              onClick={handleUserMenuOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0,
                color: mobile ? "#333" : "#1b2a6b",
                textTransform: "none",
                fontSize: mobile ? "0.95rem" : "1rem",
                fontWeight: 500,
                padding: 0,
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#1b2a6b",
                },
              }}
            >
              <AccountIcon sx={{ fontSize: "2.5rem", color: "inherit" }} />
            </Button>

            {Boolean(userMenuAnchor) && (
              <Box
                sx={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  minWidth: 250,
                  mt: 0.5,
                  zIndex: 1300,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#999", display: "block", mt: 0.5 }}
                  >
                    {user?.email}
                  </Typography>
                  {user?.role && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#1976d2",
                        fontWeight: 600,
                        display: "block",
                        mt: 0.5,
                      }}
                    >
                      {user.role}
                    </Typography>
                  )}
                </Box>
                <Box
                  component={RouterLink}
                  to={
                    user?.role === "admin"
                      ? "/admin"
                      : user?.role === "practitioner"
                      ? "/practitioner"
                      : "/patient"
                  }
                  onClick={handleUserMenuClose}
                  sx={{
                    py: 1,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    textDecoration: "none",
                    color: "#1b2a6b",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    borderBottom: "1px solid #f0f0f0",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <AccountIcon sx={{ fontSize: 20 }} />
                  <Typography variant="body2">Dashboard</Typography>
                </Box>
                <Box
                  onClick={handleLogout}
                  sx={{
                    py: 1,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    color: "#d32f2f",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#ffebee",
                    },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 20 }} />
                  <Typography variant="body2">Sign Out</Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      );
    } else {
      // User is not logged in
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: mobile ? 0.5 : 1,
            flexDirection: mobile ? "column" : "row",
            width: mobile ? "100%" : "auto",
            alignSelf: mobile ? "stretch" : "auto",
          }}
        >
          {mobile && <Divider sx={{ width: "100%", my: 1 }} />}
          <Button
            component={RouterLink}
            to="/signin"
            sx={{
              color: mobile ? "#1976d2" : "#1b2a6b",
              textTransform: "none",
              fontSize: mobile ? "0.95rem" : "1rem",
              fontWeight: 500,
              padding: mobile ? "10px 16px" : "8px 16px",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              width: mobile ? "100%" : "auto",
              justifyContent: mobile ? "center" : "flex-start",
              "&:hover": {
                backgroundColor: mobile ? "#e3f2fd" : "rgba(27, 42, 107, 0.08)",
                color: mobile ? "#1565c0" : "#1b2a6b",
              },
            }}
          >
            <LoginIcon sx={{ fontSize: 20 }} />
            Sign In
          </Button>
        </Box>
      );
    }
  };

  return (
    <>
      {/* Backdrop to close dropdowns on outside click */}
      {(Boolean(userMenuAnchor) || Boolean(labsMenuAnchor)) && (
        <Box
          onClick={() => {
            setUserMenuAnchor(null);
            setLabsMenuAnchor(null);
          }}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99,
          }}
        />
      )}
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          top: 0,
          zIndex: 100,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: { xs: 1, md: 1.5 },
              py: { xs: 1.5, md: 1 },
              flexWrap: "nowrap",
              minHeight: { xs: "auto", md: 64 },
            }}
          >
            {/* Logo */}
            <Box
              component={RouterLink}
              to="https://www.analytikaldna.com/"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                textDecoration: "none",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  opacity: 0.8,
                },
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={mainLogo}
                alt="Analytikal DNA Logo"
                sx={{
                  height: { xs: 40, md: 45 },
                  width: "auto",
                  maxWidth: 120,
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { md: 1.5, lg: 3 },
                  flexGrow: 1,
                  ml: { md: 2, lg: 4 },
                  position: "relative",
                  zIndex: 100,
                }}
              >
                <NavItems />
              </Box>
            )}

            {/* Desktop Book Test Now Button */}
            {!isMobile && (
              <Button
                component={RouterLink}
                to="https://api.whatsapp.com/send?phone=971506329298"
                target="_blank"
                variant="contained"
                sx={{
                  backgroundColor: "#2563eb",
                  color: "white",
                  textTransform: "none",
                  fontSize: { md: "0.9rem", lg: "1rem" },
                  fontWeight: 600,
                  padding: { md: "8px 16px", lg: "10px 24px" },
                  borderRadius: "6px",
                  transition: "all 0.3s ease",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  gap: 1,
                  "&:hover": {
                    backgroundColor: "#1d4ed8",
                    boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
                  },
                }}
              >
                <CalendarIcon sx={{ fontSize: 20 }} />
                Book Test Now
              </Button>
            )}

            {/* Auth Section - Desktop (1024px+) */}
            {!isMobile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AuthSection />
              </Box>
            )}

            {/* Mobile/Tablet Menu and Auth */}
            {isMobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  ml: "auto",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AuthSection mobile={false} />
                </Box>
                <IconButton
                  onClick={handleMobileMenuToggle}
                  sx={{
                    color: "#1976d2",
                  }}
                >
                  {mobileMenuOpen ? (
                    <CloseIcon fontSize="large" />
                  ) : (
                    <MenuIcon fontSize="large" />
                  )}
                </IconButton>
              </Box>
            )}
          </Toolbar>

          {/* Mobile Navigation Menu */}
          {isMobile && mobileMenuOpen && (
            <Box
              sx={{
                backgroundColor: "#f5f5f5",
                borderTop: "1px solid #e0e0e0",
                pb: 2,
                animation: "slideDown 0.3s ease",
                "@keyframes slideDown": {
                  from: {
                    opacity: 0,
                    transform: "translateY(-10px)",
                  },
                  to: {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              <Box
                sx={{
                  px: 0,
                  pt: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                }}
              >
                <NavItems mobile={true} />

                <Divider sx={{ my: 1.5, ml: 2, mr: 2 }} />

                <Box sx={{ px: 2 }}>
                  <Button
                    component={RouterLink}
                    to="https://api.whatsapp.com/send?phone=971506329298"
                    target="_blank"
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor: "#2563eb",
                      color: "white",
                      textTransform: "none",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      padding: "10px 20px",
                      gap: 1,
                      borderRadius: "6px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#1d4ed8",
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
                      },
                    }}
                  >
                    <CalendarIcon sx={{ fontSize: 20 }} />
                    Book Test Now
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Container>
      </AppBar>
    </>
  );
};

export default Header;
